<script setup lang="ts">
  import * as changeCase from 'change-case';
  import Mark from 'mark.js';
  import { storeToRefs } from 'pinia';
  import pluralize from 'pluralize';
  import { computed, inject, nextTick, onBeforeMount, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import type {
    Call,
    Email,
    EmailSender,
    EmailThread,
    Invoice,
    Partner,
    ResponseTaskData,
    Task,
  } from '@stuut/api/models';
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Button } from '@ui/components/button';
  import { ConfirmDialog } from '@ui/components/confirm-dialog';
  import { ScrollArea } from '@ui/components/scroll-area';
  import { Skeleton } from '@ui/components/skeleton';
  import { toast } from '@ui/components/sonner';
  import { Switch } from '@ui/components/switch';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { stuut } from '@/api';
  import LinkEmailDialog from '@/components/email-senders/LinkEmailDialog.vue';
  import { EmailThreadDrawer } from '@/components/email-threads';
  import RelatedTasks from '@/components/tasks/RelatedTasks.vue';
  import PaginatedTaskDrawerTable from '@/components/tasks/table/PaginatedTaskDrawerTable.vue';
  import TaskCallContext from '@/components/tasks/TaskCallContext.vue';
  import TaskCompleted from '@/components/tasks/TaskCompleted.vue';
  import TaskDismissAlert from '@/components/tasks/TaskDismissAlert.vue';
  import TaskDrawerTablePagination from '@/components/tasks/TaskDrawerTablePagination.vue';
  // import StuutLoading from '@/components/common/StuutLoading.vue';
  import TaskEmailContext from '@/components/tasks/TaskEmailContext.vue';
  import TaskManualCallView from '@/components/tasks/TaskManualCallView.vue';
  import TasksToolbar from '@/components/tasks/TasksToolbar.vue';
  import { useEmailSenders, useProfile, useTaskCollectionStore, useTaskStore } from '@/store';

  interface EvidenceQuery {
    id: string;
    text: string;
    type: string;
  }

  interface Props {
    invoices?: Invoice[];
    task?: Task;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'openDismissAlert'): void;
    (e: 'close'): void;
    (e: 'openConfirmComplete'): void;
    (e: 'enableCloseOnRouteChange'): void;
    (e: 'forceTaskTableRefresh'): void;
  }>();

  const route = useRoute();
  const router = useRouter();
  const showConfirmManualCall = ref(false);
  const saveManualCallNote = ref(true);
  const confirming = ref(false);
  const isBulkCompleting = ref(false);

  const formDataRef = ref();
  const taskStore = useTaskStore();
  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);
  const profileStore = useProfile();
  const emailSenderStore = useEmailSenders();
  const showEmailThread = ref(false);
  const { emailSenders } = storeToRefs(emailSenderStore);
  const editMode = ref(false);
  const showDismissAlert = ref(false);

  const componentTask = computed(() => props.task ?? currentTask.value);
  const dismissAlertActionType = ref<string | undefined>();

  const isSenderParticipant = computed(() => {
    const senders = emailSenders.value?.map((sender) => sender.email);
    const taskDataFrom = currentTask?.value?.responseData?.data?.from_;

    return taskDataFrom && senders?.includes(taskDataFrom);
  });

  const {
    currentCollectionTasksTotal,
    groupTaskCount,
    isIndividualTaskView,
    isTaskListMode,
    isTaskStackedMode,
    selectedCollection,
    selectedSubCollection,
    selectedTaskIndex,
  } = storeToRefs(taskCollectionStore);

  const validSenders = ref<EmailSender[]>([]);
  const TaskTableDrawerRef = ref();

  const email = ref<Email | null>(null);
  const emailThread = ref<EmailThread | null>(null);
  const call = ref<Call | null>(null);
  const partnerId = ref<string | null>(componentTask.value?.partner_id ?? null);
  const partner = ref<Partner | undefined>();
  const showConfirmDialog = ref(false);
  const showCancelAlert = ref(false);
  const isSubmitting = ref<boolean>(false);
  const closeDrawer = inject<() => void>('closeDrawer');
  const evidenceQueries = ref<EvidenceQuery[] | []>([]);
  const loadingEmail = ref(false);
  const emailId = ref<string | undefined>(componentTask.value?.emailData?.id);
  const callId = ref<string | undefined>(componentTask.value?.callData?.id);
  const linkEmailDialogRef = ref();
  const PartnerOverviewRef = ref();

  const emailThreadId = ref<string | undefined>(componentTask.value?.emailData?.thread_id);

  const openConfirmManualCall = () => {
    showConfirmManualCall.value = true;
    saveManualCallNote.value = ManualCallTaskRef.value?.isNotesDirty;
  };

  const ManualCallTaskRef = ref();

  const getQueryObjs = (arr, { prefix = '', type = '' } = {}) => {
    return arr?.map((text, index) => {
      const id = [prefix, type, index].join('_');
      return { id, text, type };
    });
  };

  const highlightText = (id?: string) => {
    const selector = `#${id || emailId.value} .prose`;
    const el = document.querySelector(selector);
    const queries = evidenceQueries.value;

    if (!el || !queries?.length) return;

    const instance = new Mark(el);

    instance.unmark({
      done: () => {
        queries?.forEach((query: EvidenceQuery) => {
          instance.mark(query?.text ?? query, {
            acrossElements: true,
            className: `bg-highlight-muted ${query?.id}`,
            ignorePunctuation: [',', ',', "'", '"'],
            separateWordSearch: false,
          });
        });
      },
    });
  };

  const openConfirmDialog = () => {
    showConfirmDialog.value = true;
  };

  const closeConfirmDialog = () => {
    showConfirmDialog.value = false;
  };

  const openDismissAlert = (actionType?: string) => {
    showDismissAlert.value = true;
    dismissAlertActionType.value = actionType;
  };

  const closeDismissAlert = () => {
    showDismissAlert.value = false;
    dismissAlertActionType.value = undefined;
  };

  const dismissAlertData = computed(() => {
    if (componentTask.value?.isManualCall) {
      const notes = ManualCallTaskRef.value?.notes;
      if (notes) return { manual_call_notes: notes };
    }
    return undefined;
  });

  const handleAcknowledge = async () => {
    if (!componentTask.value) return;

    isSubmitting.value = true;

    try {
      const response = await stuut.tasks.acknowledge(componentTask.value as Task);

      toast.success('Acknowledged task and marked complete.');

      taskCollectionStore.afterCompleteTask();
      taskCollectionStore.refreshAllTaskTables();

      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isSubmitting.value = false;
    }
  };

  const handleConfirmClick = async () => {
    if (!componentTask.value) return;

    isSubmitting.value = true;

    try {
      const result = await formDataRef.value?.formData.validate();

      if (!result.valid) {
        const invalidFields = Object.keys(result.results)
          .filter((key) => result.results[key].valid === false)
          .map((field) => changeCase.capitalCase(field).split(' ')[0]);

        toast.error(
          `Could not send email. Fix these fields and retry: ${invalidFields.join(', ')}`,
        );

        return;
      }

      const response = await stuut.tasks.sendNewResponse(
        componentTask.value,
        {
          ...formDataRef.value?.formData.values,
          from_: formDataRef.value?.formData.values.from,
        },
        componentTask.value?.responseData as ResponseTaskData,
      );

      toast.success('Email response approved.');

      taskCollectionStore.afterCompleteTask();
      taskCollectionStore.refreshAllTaskTables();

      closeConfirmDialog();

      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isSubmitting.value = false;
    }
  };

  const loadEmail = async () => {
    loadingEmail.value = true;

    try {
      if (emailId.value) {
        email.value = (await taskStore.peekFindEmail(emailId.value)) || null;
      }

      if (emailThreadId.value) {
        emailThread.value = await taskStore.peekFindEmailThread(emailThreadId.value);

        const participantEmails = emailThread.value?.getParticipants();

        validSenders.value = emailSenders.value?.filter(
          (sender) =>
            participantEmails?.includes(sender.email) || email.value?.hasRemoteLink(sender),
        );
      } else {
        validSenders.value = emailSenders.value;
      }

      if (callId.value) {
        call.value = await taskStore.peekFindCall(callId.value);
      }

      if (partnerId.value) {
        partner.value = await taskStore.peekFindPartner(partnerId.value);
      }

      nextTick(async () => {
        highlightText();
      });
    } catch (error) {
      console.error(error);
    } finally {
      loadingEmail.value = false;
    }
  };

  onBeforeMount(async () => {
    await loadEmail();

    evidenceQueries.value = [
      ...(getQueryObjs(componentTask.value?.classificationCallouts, {
        prefix: componentTask.value?.callData?.id,
        type: 'callout',
      }) || []),
      ...(getQueryObjs(componentTask.value?.classificationKeywords, {
        prefix: componentTask.value?.callData?.id,
        type: 'keyword',
      }) || []),
    ];

    nextTick(() => {
      highlightText();
    });

    if (partnerId.value) {
      partner.value = await taskCollectionStore.loadCurrentPartner(partnerId.value);
    }

    await emailSenderStore.fetchEmailSenders();
  });

  const toggleEditMode = () => {
    if (editMode.value) {
      editModeOff();
    } else {
      editModeOn();
    }
  };

  const editModeOff = () => {
    editMode.value = false;

    PartnerOverviewRef.value?.open();
    nextTick(() => highlightText());
  };

  const editModeOn = () => {
    editMode.value = true;

    PartnerOverviewRef.value?.close();
  };

  // const isBulkReplyRequired = computed(() =>
  //   currentCollectionTasks.value?.some((task) => task.isReplyRequired),
  // );

  const bulkComplete = async (
    actionType?: 'acknowledge' | 'complete' | 'complete_with_action',
    taskType?: string,
  ) => {
    if (!actionType) return;

    isBulkCompleting.value = true;

    try {
      const response = await stuut.tasks.bulkCompleteAction(
        TaskTableDrawerRef?.value?.selectedItems?.map((task) => task.id),
        actionType,
        taskType || undefined,
        TaskTableDrawerRef?.value?.selectedItems,
      );

      toast.success(`Processing ${TaskTableDrawerRef?.value?.selectedItems?.length} tasks`);

      taskCollectionStore.afterCompleteTask();
      taskCollectionStore.refreshAllTaskTables();

      return response;
    } catch (err: any) {
      toast.error(err?.clientMessage || 'An error occurred');
    } finally {
      isBulkCompleting.value = false;
    }
  };

  const animationDir = computed(() => {
    switch (taskCollectionStore.navigationDirection) {
      case 'backward':
        return 'task-content-backward';
      case 'forward':
        return 'task-content-forward';
      default:
        return '';
    }
  });

  const completeManualCall = async () => {
    if (!currentTask?.value) return;

    confirming.value = true;

    try {
      confirming.value = true;

      const response = await stuut.tasks.complete(currentTask?.value!, {
        close_reason: ManualCallTaskRef.value?.notes,
      });

      toast.success('Task completed');

      if (saveManualCallNote.value && currentTask.value?.partner_id) {
        await stuut.notes.create({
          content: ManualCallTaskRef.value?.notes,
          partner: currentTask.value?.partner_id,
        });
      }

      taskCollectionStore.afterCompleteTask();
      taskCollectionStore.refreshAllTaskTables();

      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      showConfirmManualCall.value = false;
      confirming.value = false;
    }
  };

  const isNewEmail = () =>
    !email.value?.id &&
    (currentTask.value?.isCallSource || currentTask.value?.isBrokenPromiseFollowUp);

  defineExpose({
    ManualCallTaskRef,
    TaskTableDrawerRef,
  });
</script>

<template>
  <div
    class="h-full flex flex-1 flex-col text-muted-foreground bg-secondary overflow-hidden relative rounded z-10"
    style="transition: width 250ms cubic-bezier(0.4, 0, 0.2, 1)"
  >
    <!--    <div class="flex h-full overflow-hidden flex-1 rounded"> -->
    <ScrollArea
      class="flex-1 h-full [&>div]:outline-transparent @container [&>div]:outline-none border-l"
      :disabled="editMode"
      :class="{
        'pr-5 py-1': !editMode && !taskCollectionStore.isLoadingTask,
        'px-5': !editMode && isIndividualTaskView,
      }"
    >
      <!--        <div class="h-full"> -->
      <TaskCompleted
        v-if="!isTaskListMode && !currentTask?.isOpen"
        class="mb-5"
        :task="currentTask"
      />

      <template v-if="isTaskListMode">
        <Transition name="table-fade" mode="out-in" appear>
          <PaginatedTaskDrawerTable
            ref="TaskTableDrawerRef"
            :key="`${route.query.group_view_id}${route.query.collection_type}${route.query.collection_id}${route.query.sub_collection_type}${route.query.sub_collection_id}${partner?.id}`"
            :partner="taskCollectionStore?.currentPartner as Partner"
          />
        </Transition>
      </template>

      <template v-else>
        <div v-if="taskCollectionStore.isLoadingTask" class="flex-1 space-y-8 w-full bg-secondary">
          <Skeleton class="w-full h-5 flex-1 rounded" />

          <div
            class="text-xs flex flex-nowrap items-center w-full h-40 place-content-center space-x-4 relative"
          >
            <Skeleton class="h-40 flex-1 rounded" />

            <div
              class="absolute text-xs flex flex-nowrap items-center w-full place-content-center space-x-4"
            >
              <!-- <StuutLoading /> -->

              <div class="space-y-1">
                <strong>Hang tight!</strong>
                <div class="text-muted-foreground">Loading your task...</div>
              </div>
            </div>
          </div>

          <Skeleton class="w-20 h-5 flex-1 rounded" />

          <div class="flex-1 space-y-4">
            <Skeleton class="h-5 flex-1 rounded" />
            <Skeleton class="h-5 flex-1 rounded" />
            <Skeleton class="h-5 flex-1 rounded" />
          </div>
        </div>

        <Transition v-else :name="animationDir" mode="out-in" appear>
          <div
            :key="`task-content-${componentTask?.id}`"
            class="bg-background text-xs h-full w-full space-y-8"
            :class="{
              'p-2 border': !editMode,
              'p-0 border-l': editMode,
            }"
            data-section="task-details"
          >
            <Transition name="shrink">
              <div v-if="!editMode" class="space-y-8">
                <div
                  class="transition-all origin-center space-y-4"
                  :class="editMode && 'h-full border-transparent'"
                >
                  <div
                    class="bg-background"
                    :class="editMode ? 'border-transparent h-full' : 'rounded'"
                  >
                    <TaskManualCallView
                      v-if="componentTask?.taskComponentType === 'manual_call'"
                      ref="ManualCallTaskRef"
                      :key="`${componentTask?.id}-manual-call`"
                      @enable-close-on-route-change="emit('enableCloseOnRouteChange')"
                      @open-dismiss-alert="emit('openDismissAlert')"
                      @open-confirm-complete="emit('openConfirmComplete')"
                      @force-task-table-refresh="emit('forceTaskTableRefresh')"
                      @close="emit('close')"
                    />
                  </div>
                </div>
              </div>
            </Transition>

            <div v-if="['email', 'call'].includes(currentTask?.source as string)">
              <TaskEmailContext
                v-if="currentTask?.isEmailSource"
                :email="email as Email"
                :email-thread="emailThread as EmailThread"
              />

              <TaskCallContext v-if="currentTask?.isCallSource" />
            </div>
          </div>
        </Transition>
      </template>
      <!--        </div> -->
    </ScrollArea>
    <!--    </div> -->

    <div
      class="bg-background flex flex-nowrap items-center justify-between py-2 border-t h-14"
      :class="{
        'pl-5 pr-10': route.query.group_view_mode === 'partner',
        'px-5': route.query.group_view_mode !== 'partner',
      }"
      data-section="task-footer"
    >
      <div class="flex items-center space-x-2.5 flex-1">
        <template v-if="isTaskListMode">
          <ConfirmDialog
            v-if="route.query.group_view_id === 'auto_response_email'"
            confirm-text="Send"
            @confirm="bulkComplete('complete_with_action', 'auto_response_email')"
          >
            <template #title>
              Send
              {{ TaskTableDrawerRef?.selectedItems?.length }}
              {{ pluralize('email', TaskTableDrawerRef?.selectedItems?.length) }}?
            </template>
            <template #description> This action cannot be undone. Please confirm. </template>

            <template #trigger>
              <Button
                :disabled="
                  !componentTask?.isOpen || TaskTableDrawerRef?.selectedItems <= 0 || isSubmitting
                "
                size="sm"
              >
                <span>Bulk send</span>
              </Button>
            </template>
          </ConfirmDialog>

          <ConfirmDialog
            confirm-text="Acknowledge"
            @confirm="
              bulkComplete(
                taskCollectionStore?.selectedSubCollection?.expectedAction,
                taskCollectionStore?.selectedCollectionId,
              )
            "
          >
            <template #title>
              Acknowledge
              {{ TaskTableDrawerRef?.selectedItems?.length }}
              {{ pluralize('task', TaskTableDrawerRef?.selectedItems?.length) }}?
            </template>

            <template #description> Please confirm. </template>

            <template #trigger>
              <Button
                v-if="taskCollectionStore?.selectedSubCollection?.isExpectsAcknowledge"
                :disabled="
                  !componentTask?.isOpen ||
                  TaskTableDrawerRef?.selectedItems <= 0 ||
                  isBulkCompleting ||
                  isSubmitting
                "
                size="sm"
              >
                <i class="fas fa-thumbs-up text-xs" />
                Acknowledge!
              </Button>
            </template>
          </ConfirmDialog>

          <TaskDrawerTablePagination
            v-if="TaskTableDrawerRef?.table"
            :table="TaskTableDrawerRef?.table"
            :global-selection="TaskTableDrawerRef?.selectedItems"
            :total-records="TaskTableDrawerRef?.totalRecords"
            @clear-selection="TaskTableDrawerRef?.clearSelectedItems"
          >
            <template #actions>
              <TasksToolbar
                :tasks="TaskTableDrawerRef?.selectedItems"
                button-text="Other actions"
                @clear-selection="TaskTableDrawerRef?.clearSelectedItems"
              />
            </template>
          </TaskDrawerTablePagination>
        </template>

        <template v-else-if="isTaskStackedMode">
          <!-- Send -->
          <Button
            v-if="
              !profileStore.profile.default_email_sender_id &&
              componentTask?.isReplyRequired &&
              componentTask?.responseData
            "
            variant="agent"
            size="sm"
            :disabled="isSubmitting"
            @click="linkEmailDialogRef.open()"
          >
            Link Email To Respond
          </Button>

          <Tooltip v-if="componentTask?.isReplyRequired" :disabled="Boolean(isSenderParticipant)">
            <TooltipTrigger as-child>
              <Button
                class=""
                size="sm"
                :disabled="
                  !componentTask?.isOpen || (!isNewEmail && !isSenderParticipant) || isSubmitting
                "
                @click="openConfirmDialog"
              >
                <template v-if="componentTask?.isProcessing">
                  <i class="fa-regular fa-spinner text-xs animate-spin"></i>Sending...
                </template>

                <template v-else>
                  <i class="far fa-paper-plane"></i>
                  Send
                </template>
              </Button>
            </TooltipTrigger>

            <TooltipContent>
              <template v-if="!isSenderParticipant">
                You cannot respond because you are not a participant on this thread.
              </template>
            </TooltipContent>
          </Tooltip>

          <!-- Acknowledge -->
          <Button
            v-else-if="componentTask?.taskComponentType === 'acknowledge'"
            size="sm"
            :disabled="!componentTask?.isOpen || isSubmitting"
            :loading="isSubmitting"
            @click="handleAcknowledge"
          >
            <i class="fas fa-thumbs-up text-xs" />
            Acknowledge!
          </Button>

          <!-- Mark as complete -->
          <Button
            v-else-if="componentTask?.taskComponentType === 'manual_call'"
            variant="warning"
            size="sm"
            :disabled="!componentTask?.isOpen || isSubmitting"
            :loading="isSubmitting"
            @click="openConfirmManualCall"
          >
            <i class="fas fa-check text-xs"></i>
            <span>Mark as complete</span>
          </Button>

          <!-- Dismiss -->
          <Tooltip :delay-duration="0">
            <TooltipTrigger as-child>
              <Button
                variant="secondary"
                :disabled="!componentTask?.isOpen || isSubmitting"
                @click="openDismissAlert('close')"
              >
                <span class="fa-stack">
                  <i class="fa-solid fa-slash fa-stack-1x"></i>
                  <i class="fas fa-list-ul fa-stack-1x"></i>
                </span>

                <span>Dismiss</span>
              </Button>
            </TooltipTrigger>

            <TooltipContent> Mark task as closed </TooltipContent>
          </Tooltip>
        </template>
      </div>

      <div class="flex items-center space-x-2.5">
        <template v-if="isTaskStackedMode">
          <template v-if="componentTask?.isReplyRequired">
            <Tooltip v-if="editMode">
              <TooltipTrigger as-child>
                <Button
                  size="sm"
                  variant="outline"
                  :disabled="componentTask?.isProcessing"
                  @click="toggleEditMode"
                >
                  <i class="far fa-arrow-left"></i>
                  View full task
                </Button>
              </TooltipTrigger>

              <TooltipContent> Exit full panel edit mode </TooltipContent>
            </Tooltip>

            <Tooltip v-else :delay-duration="0">
              <TooltipTrigger as-child>
                <Button
                  size="sm"
                  variant="outline"
                  :disabled="!(componentTask?.isOpen || componentTask.isCompleted)"
                  @click="toggleEditMode"
                >
                  <template v-if="componentTask.isCompleted">
                    <i class="far fa-eye"></i>
                    View sent response
                  </template>

                  <template v-else-if="componentTask.isOpen">
                    <i class="far fa-pen-to-square"></i>
                    Edit mode
                  </template>
                </Button>
              </TooltipTrigger>

              <TooltipContent> Full panel edit mode </TooltipContent>
            </Tooltip>
          </template>

          <RelatedTasks v-if="isTaskStackedMode && componentTask?.related_tasks?.length" />

          <div v-if="selectedTaskIndex >= 0" class="w-14 text-center">
            {{ (selectedTaskIndex || 0) + 1 }}

            <template v-if="isIndividualTaskView && currentCollectionTasksTotal">
              of
              {{ currentCollectionTasksTotal }}
            </template>

            <template
              v-else-if="
                selectedSubCollection?.task_count ||
                selectedCollection?.task_count ||
                groupTaskCount
              "
            >
              of
              {{
                selectedSubCollection?.task_count ||
                selectedCollection?.task_count ||
                groupTaskCount
              }}
            </template>
          </div>

          <Button
            class="shrink-0 group"
            size="icon"
            variant="outline"
            @click="taskCollectionStore.gotoPreviousTask(router, route)"
          >
            <i class="far fa-arrow-left text-2xs! group-hover:text-foreground"></i>
          </Button>

          <Button
            class="shrink-0 group"
            size="icon"
            variant="outline"
            @click="taskCollectionStore.gotoNextTask()"
          >
            <i class="far fa-arrow-right text-2xs! group-hover:text-foreground"></i>
          </Button>
        </template>

        <Button class="shrink-0 group" size="icon" variant="outline" @click="closeDrawer">
          <i class="far fa-close text-2xs! group-hover:text-foreground"></i>
        </Button>
      </div>
    </div>
  </div>

  <TaskDismissAlert
    v-if="componentTask"
    :task="componentTask"
    :show="showDismissAlert"
    :action-type="dismissAlertActionType"
    :data="dismissAlertData"
    @close="closeDismissAlert"
  />

  <AlertDialog :open="showConfirmDialog" @update:open="(status) => (showConfirmDialog = status)">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Send response</AlertDialogTitle>
        <AlertDialogDescription>
          <span>Are you sure you want to send this response and complete the task?</span>
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel :loading="isSubmitting" @click="closeConfirmDialog">
          Cancel
        </AlertDialogCancel>

        <AlertDialogAction :loading="isSubmitting" @click="handleConfirmClick">
          Send
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <AlertDialog
    :open="showConfirmManualCall"
    @update:open="(status) => (showConfirmManualCall = status)"
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle> Complete Task </AlertDialogTitle>

        <AlertDialogDescription class="space-y-5">
          <p>Do you want to mark this task as complete?</p>

          <div class="space-y-4 px-3.5 pb-3.5 border-2 rounded max-h-34 overflow-y-auto">
            <p class="font-bold text-muted-foreground sticky top-0 pt-3.5 pb-0.5 bg-background">
              Notes:
            </p>

            <div class="whitespace-pre-line">
              {{ ManualCallTaskRef?.notes }}
            </div>
          </div>

          <div class="flex items-center gap-x-2.5 justify-start">
            <span class="text-xs">Save note to customer page</span>

            <Switch
              name="saveManualCallNote"
              :model-value="saveManualCallNote"
              @update:model-value="(event) => (saveManualCallNote = event)"
            />
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter class="space-x-2.5">
        <AlertDialogCancel :disabled="confirming" @click="showConfirmManualCall = false">
          Cancel
        </AlertDialogCancel>

        <AlertDialogAction
          class="bg-warning text-foreground"
          :disabled="confirming"
          :loading="confirming"
          @click="completeManualCall"
        >
          <i class="fas fa-check text-xs"></i>
          <span>Mark as complete</span>
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <AlertDialog :open="showCancelAlert" @update:open="(status) => (showCancelAlert = status)">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
        <AlertDialogDescription>
          <span> Are you sure you want to cancel? You will lose any unsaved work </span>
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel @click="showCancelAlert = false"> Cancel </AlertDialogCancel>

        <AlertDialogAction @click="toggleEditMode"> Discard changes </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <LinkEmailDialog ref="linkEmailDialogRef" />

  <EmailThreadDrawer
    v-if="emailThread && showEmailThread"
    v-model:open="showEmailThread"
    :thread="emailThread"
    :selected-email-id="email?.id"
    is-slider
    default-open
    class="h-full min-h-full"
    @close="
      () => {
        emailThread = null;
        showEmailThread = false;
      }
    "
  />
</template>

<style>
  :root {
    --left-width: 250px;
  }
</style>

<style scoped>
  .text-primary mark {
    color: var(--agent);
  }

  .bg-blue-100 mark {
    background-color: var(--blue);
    color: var(--blue);
  }

  .grow-enter-active {
    transition: all 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  .grow-leave-active {
    transition: all 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .grow-enter-from,
  .grow-leave-to {
    max-height: 0;
    opacity: 0;
    transform: translateY(-10px);
  }

  .grow-enter-to,
  .grow-leave-from {
    max-height: 300px;
    opacity: 1;
    transform: translateY(0);
  }

  .shrink-enter-active {
    transition: all 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
    overflow: hidden;
  }

  .shrink-leave-active {
    transition: all 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
    overflow: hidden;
  }

  .shrink-enter-from,
  .shrink-leave-to {
    max-height: 0;
    opacity: 0;
    transform: translateY(-10px);
  }

  .shrink-enter-to,
  .shrink-leave-from {
    max-height: 300px;
    opacity: 1;
    transform: translateY(0);
  }

  /* Forward task content transition animations */
  .task-content-forward-enter-active {
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  .task-content-forward-leave-active {
    transition: all 0.2s cubic-bezier(0.55, 0.085, 0.68, 0.53);
  }

  .task-content-forward-enter-from {
    opacity: 0;
    transform: translateX(100%) scale(1);
  }

  .task-content-forward-leave-to {
    opacity: 0;
    transform: translateX(-100%) scale(1);
  }

  .task-content-forward-enter-to,
  .task-content-forward-leave-from {
    opacity: 1;
    transform: translateX(0) translateY(0) scale(1);
  }

  /* Backward task content transition animations */
  .task-content-backward-enter-active {
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  .task-content-backward-leave-active {
    transition: all 0.2s cubic-bezier(0.55, 0.085, 0.68, 0.53);
  }

  .task-content-backward-enter-from {
    opacity: 0;
    transform: translateX(-100%) scale(1);
  }

  .task-content-backward-leave-to {
    opacity: 0;
    transform: translateX(100%) scale(1);
  }

  .task-content-backward-enter-to,
  .task-content-backward-leave-from {
    opacity: 1;
    transform: translateX(0) translateY(0) scale(1);
  }

  /* Table fade transition animations */
  .table-fade-enter-active,
  .table-fade-leave-active {
    transition: opacity 0.3s ease-in-out;
  }

  .table-fade-enter-from,
  .table-fade-leave-to {
    opacity: 0;
  }

  .table-fade-enter-to,
  .table-fade-leave-from {
    opacity: 1;
  }
</style>
